import React, { useState, useEffect } from 'react'
import { useImproveCycleStore } from '../../store/improveCycleStore'
import { Save, CheckCircle, AlertCircle } from 'lucide-react'

const PlanificarForm = ({ ciclo }) => {
  const { savePlanificar, completarPlanificar, loading } = useImproveCycleStore()
  const [formData, setFormData] = useState({
    titulo_proyecto: '',
    descripcion_detallada: '',
    analisis_causa_raiz: '',
    causa_raiz_seleccionada: '',
    esfuerzo: 3,
    impacto: 3,
    alcance_piloto: '',
    objetivo_especifico: '',
    metrica_medir: '',
    meta_numerica: '',
    plazo: '',
    metrica_baseline: '',
    recursos_humanos: '',
    recursos_financieros: 0,
    restricciones_riesgos: '',
    plan_accion: []
  })
  const [mensaje, setMensaje] = useState(null)
  const [camposFaltantes, setCamposFaltantes] = useState([])

  useEffect(() => {
    if (ciclo?.fases?.planificar) {
      setFormData({
        ...ciclo.fases.planificar,
        plan_accion: ciclo.fases.planificar.plan_accion || []
      })
    }
  }, [ciclo])

  const handleChange = (e) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }))
  }

  const handleSave = async () => {
    try {
      const result = await savePlanificar(ciclo.id, formData)
      setCamposFaltantes(result.campos_faltantes || [])
      setMensaje({ type: 'success', text: 'Guardado exitosamente' })
      setTimeout(() => setMensaje(null), 3000)
    } catch (error) {
      setMensaje({ type: 'error', text: error.message })
    }
  }

  const handleCompletar = async () => {
    if (camposFaltantes.length > 0) {
      setMensaje({ 
        type: 'error', 
        text: `Completa los campos faltantes: ${camposFaltantes.join(', ')}` 
      })
      return
    }

    try {
      await completarPlanificar(ciclo.id)
      setMensaje({ type: 'success', text: 'Fase Planificar completada. Fase Hacer desbloqueada!' })
    } catch (error) {
      setMensaje({ type: 'error', text: error.message })
    }
  }

  const puedeCompletar = camposFaltantes.length === 0 && !ciclo?.fases?.planificar?.completado

  return (
    <div className="space-y-6">
      {/* Mensaje */}
      {mensaje && (
        <div className={`
          p-4 rounded-lg flex items-start gap-3
          ${mensaje.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}
        `}>
          {mensaje.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          )}
          <p className={mensaje.type === 'success' ? 'text-green-700' : 'text-red-700'}>
            {mensaje.text}
          </p>
        </div>
      )}

      {/* Sección A: Definición del Problema */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">A. Definición del Problema</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título del Proyecto *
            </label>
            <input
              type="text"
              name="titulo_proyecto"
              value={formData.titulo_proyecto}
              onChange={handleChange}
              disabled={ciclo?.fases?.planificar?.completado}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="Ej: Reducir tiempo de espera en urgencias"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción Detallada *
            </label>
            <textarea
              name="descripcion_detallada"
              value={formData.descripcion_detallada}
              onChange={handleChange}
              disabled={ciclo?.fases?.planificar?.completado}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="Describe el problema en detalle..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Análisis de Causa Raíz *
            </label>
            <textarea
              name="analisis_causa_raiz"
              value={formData.analisis_causa_raiz}
              onChange={handleChange}
              disabled={ciclo?.fases?.planificar?.completado}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="¿Por qué ocurre este problema?"
            />
          </div>
        </div>
      </div>

      {/* Sección B: Priorización */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">B. Priorización y Alcance</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Causa Raíz Seleccionada *
            </label>
            <input
              type="text"
              name="causa_raiz_seleccionada"
              value={formData.causa_raiz_seleccionada}
              onChange={handleChange}
              disabled={ciclo?.fases?.planificar?.completado}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Esfuerzo (1-5) *
              </label>
              <input
                type="number"
                name="esfuerzo"
                min="1"
                max="5"
                value={formData.esfuerzo}
                onChange={handleChange}
                disabled={ciclo?.fases?.planificar?.completado}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Impacto (1-5) *
              </label>
              <input
                type="number"
                name="impacto"
                min="1"
                max="5"
                value={formData.impacto}
                onChange={handleChange}
                disabled={ciclo?.fases?.planificar?.completado}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alcance del Piloto *
            </label>
            <textarea
              name="alcance_piloto"
              value={formData.alcance_piloto}
              onChange={handleChange}
              disabled={ciclo?.fases?.planificar?.completado}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="Define el alcance del piloto..."
            />
          </div>
        </div>
      </div>

      {/* Sección C: Objetivos SMART */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">C. Objetivos SMART y KPIs</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Objetivo Específico *
            </label>
            <input
              type="text"
              name="objetivo_especifico"
              value={formData.objetivo_especifico}
              onChange={handleChange}
              disabled={ciclo?.fases?.planificar?.completado}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="Ej: Reducir el tiempo promedio de espera de 45 a 15 minutos"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Métrica a Medir *
              </label>
              <input
                type="text"
                name="metrica_medir"
                value={formData.metrica_medir}
                onChange={handleChange}
                disabled={ciclo?.fases?.planificar?.completado}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Ej: Tiempo promedio de espera (minutos)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plazo *
              </label>
              <input
                type="text"
                name="plazo"
                value={formData.plazo}
                onChange={handleChange}
                disabled={ciclo?.fases?.planificar?.completado}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Ej: 30 días"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Numérica *
              </label>
              <input
                type="number"
                name="meta_numerica"
                value={formData.meta_numerica}
                onChange={handleChange}
                disabled={ciclo?.fases?.planificar?.completado}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="15"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Métrica Baseline *
              </label>
              <input
                type="number"
                name="metrica_baseline"
                value={formData.metrica_baseline}
                onChange={handleChange}
                disabled={ciclo?.fases?.planificar?.completado}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="45"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Campos faltantes */}
      {camposFaltantes.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-800 mb-2">Campos faltantes:</h4>
          <ul className="list-disc list-inside text-sm text-yellow-700">
            {camposFaltantes.map((campo, index) => (
              <li key={index}>{campo}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Botones */}
      {!ciclo?.fases?.planificar?.completado && (
        <div className="flex gap-4">
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            Guardar Borrador
          </button>

          <button
            onClick={handleCompletar}
            disabled={loading || !puedeCompletar}
            className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-5 h-5" />
            Completar Planificación
          </button>
        </div>
      )}

      {ciclo?.fases?.planificar?.completado && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700 font-semibold">
            ✓ Fase Planificar completada
          </p>
        </div>
      )}
    </div>
  )
}

export default PlanificarForm
